import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withSnackbar } from 'notistack';

import { formatPhoneNumber } from 'utils/utils';
import { canByLicensing } from 'utils/licensing/licensingChecker';
import FileUpload from 'components/CustomUpload/FileUpload';
import SnackMessage from 'components/Snackbar/SnackMessage';

const validationCsv = (name) => name.slice(-4) === '.csv';

class MarketingUploadCsv extends Component {
	state = {
		hasFile: false,
		fileName: '',
		isError: false,
		errorText: '',
		isTypeAllowed: {
			broadcast: false,
			multicast: false,
		},
	};

	fileUpload = React.createRef();

	componentDidMount() {
		this.setAllowedTypes();
	}

	setAllowedTypes = () => {
		if (canByLicensing('marketing.broadcast')) {
			this.setState(({ isTypeAllowed }) => {
				return { isTypeAllowed: { ...isTypeAllowed, broadcast: true } }
			});
		}
		if (canByLicensing('marketing.multicast')) {
			this.setState(({ isTypeAllowed }) => {
				return { isTypeAllowed: { ...isTypeAllowed, multicast: true } }
			});
		}
	}

	onClear = () => {
		this.setState({
			hasFile: false,
			fileName: '',
			errorText: '',
			isError: false,
		});
		this.props.setCsvData(null);
	}

	showMessage = (message, color = 'danger') => {
		this.props.enqueueSnackbar(message, {
			content: (key, message) => <SnackMessage id={key} message={message} color={color} />,
		});
	}

	parseCsv = (data) => {
		const { isTypeAllowed } = this.state;

		const rows = data ? data.split(/\r\n|\n|\r/) : [];
		let message = rows[0].trim();

		message = message.replace(/^"(.+(?="$))"$/, '$1');
		while (message.includes('""')) {
			message = message.replace('""', '"');
		}
		while (message.includes(';')) {
			message = message.replace(';', '');
		}

		const unverifiedPhones = rows.slice(1).filter(Boolean);
		const phones = new Set();

		for (let i = 0; i < unverifiedPhones.length; i += 1) {
			const phone = formatPhoneNumber(unverifiedPhones[i]);
			if (phone && phone.length >= 11 && phone.length <= 14) {
				phones.add(phone);
			}
		}

		const isAllInvalid = phones.size === 0 && unverifiedPhones.length !== 0;

		if (!isTypeAllowed.broadcast && unverifiedPhones.length === 0 && message) {
			this.showMessage('Broadcast is not available based on your license. Please, add at least one phone number to the file');

			this.setState({
				isError: true,
				errorText: 'File is not uploaded. Please, add at least one phone number to your file',
			});
			return;
		}

		if (!isTypeAllowed.multicast && unverifiedPhones.length !== 0 && message) {
			this.showMessage('Multicast is not available based on your license.');

			this.setState({
				isError: true,
				errorText: 'File is not uploaded.',
			});
			return;
		}

		if (!isTypeAllowed.broadcast && !isTypeAllowed.multicast) {
			this.showMessage('This Feature is not available based on your license');

			this.setState({
				isError: true,
				errorText: 'This Feature is not available based on your license',
			});
			return;
		}

		if (message && !isAllInvalid) {
			this.props.setCsvData({
				message,
				phones: Array.from(phones),
			});
			this.onAfterCsvParse(phones.size);
		} else {
			this.props.setCsvData(null);

			let messageErrorText;
			let errorText;

			if (isAllInvalid) {
				messageErrorText = 'All phone numbers are invalid. Please, check the file and try again';
				errorText = 'Notice: all phone numbers are invalid';
			} else if (!message && !isAllInvalid) {
				messageErrorText = 'The Message is empty. Please, try again';
				errorText = 'Notice: .csv Message cannot be empty';
			} else {
				messageErrorText = 'The File is empty. Please, try again';
				errorText = 'Notice: .csv File cannot be empty';
			}

			this.showMessage(messageErrorText);

			this.setState({
				isError: true,
				errorText,
			});
		}
	}

	onAfterCsvParse = (phoneNumbersCount = 0) => {
		const title = phoneNumbersCount > 0
			? `Your Marketing Update will be sent to the ${phoneNumbersCount} Customers`
			: 'Notice your Marketing Update will be sent to the All registered Customers from the \'Available Countries\' list';

		this.props.setAlert({
			title,
			type: 'warning',
			onConfirm: () => this.props.setAlert(null),
			onCancel: () => {
				this.props.setAlert(null);
				this.handleClear();
			},
		});
	}

	handleChange = (event) => {
		event.preventDefault();
		const file = event.target.files[0];
		if (file) {
			const fileName = file.name;

			if (!validationCsv(fileName)) {
				this.setState({
					isError: true,
					errorText: 'Notice: File Type must be .csv',
					fileName: '',
					hasFile: false,
				});
				this.props.setCsvData(null);
				return;
			}

			this.setState({ fileName, isError: false, errorText: '' });

			const fileReader = new FileReader();
			fileReader.onload = (readerEvent) => {
				const { result } = readerEvent.target;
				this.parseCsv(result);
				this.setState({ hasFile: true });
			};
			fileReader.readAsText(file);
		}
	}

	handleClear = () => {
		this.fileUpload.current.handleClear();
	}

	render() {
		return (
			<FileUpload
				ref={this.fileUpload}
				tooltip='Click to upload .csv'
				allowedFormats='.csv'
				label='Upload .csv File with the List of Customers and Broadcast Text'
				helperText='File Type:.csv. First Line should contain Broadcast Text'
				errorText={this.state.errorText}
				isError={this.state.isError}
				hasFile={this.state.hasFile}
				fileName={this.state.fileName}
				handleChange={this.handleChange}
				onClear={this.onClear}
			/>
		);
	}
}

MarketingUploadCsv.propTypes = {
	setCsvData: PropTypes.func,
	setAlert: PropTypes.func,
};

export default withSnackbar(MarketingUploadCsv);
